import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser } from '../slices/userSlice';
import { switchActiveRole } from '../slices/userRoleSlice';
import { Button, Input, Message } from "semantic-ui-react";
import { isRegistrationEnabled } from '../utils/featureFlags';
import { readAuthBreadcrumbs, readDeliberateSignOut, clearDeliberateSignOut } from '../apiTools/apiClient';
import { isTokenLive } from '../isLoggedIn';
import '../index.css';

/**
 * ─── ⭐ WHY YOU WERE SENT HERE, SHOWN WHERE YOU LAND ─────────────────────────────────────────────
 *
 * The operator's report, and the reason this panel exists: *"there is an error message flashed, but
 * the redirect to login is so fast I can't read it and the log is cleared too."* Which is exactly
 * right — `window.location.assign` is a full page load, so the console goes with it unless DevTools
 * has "Preserve log" ticked, and the flash is gone before it can be read.
 *
 * So the last 401 is written to localStorage BEFORE the redirect (see recordAuthBreadcrumb) and read
 * back here. Nothing to catch, nothing to enable, no console needed.
 *
 * ⚠️ Recent ones only. A crumb from last Tuesday explains nothing about this login and would just be
 * furniture; if it is stale the panel stays hidden.
 */
const CRUMB_FRESH_MS = 15 * 60 * 1000;

/**
 * ⛔ PAGES WHERE HAVING NO TOKEN IS THE NORMAL STATE. A 401 on a request made from here says only
 * that a signed-out page made a request, which is what a signed-out page does. It is never the
 * reason a session ended, so it must not be offered as one.
 */
const SESSIONLESS_PAGES = ['/login', '/logout'];

function WhySignedOut() {
  /**
   * ⭐ A DELIBERATE SIGN-OUT SILENCES THE PANEL (2026-10-26). Read here, cleared in the effect
   * below — see readDeliberateSignOut in apiClient.js for why those are two calls and not one.
   */
  const deliberate = React.useMemo(() => readDeliberateSignOut(), []);
  React.useEffect(() => {
    if (deliberate) clearDeliberateSignOut();
  }, [deliberate]);

  const crumb = React.useMemo(() => {
    if (deliberate) return null;
    const all = readAuthBreadcrumbs();
    /**
     * ⚠️ THE NEWEST CRUMB IS NOT NECESSARILY THE RELEVANT ONE. On 2026-10-26 the newest was a bare
     * `/login-allowlist/me` from the navbar's AllowlistBadge, which had raced the logout and gone
     * out after the token was removed — so the panel explained a chosen sign-out with a 401 the
     * sign-out itself had caused. Walk back to the newest crumb that could describe a loss.
     */
    const last = all
      .filter(c => c && c.at && !SESSIONLESS_PAGES.includes(c.page))
      .pop();
    if (!last) return null;
    return Date.now() - new Date(last.at).getTime() < CRUMB_FRESH_MS ? last : null;
  }, [deliberate]);
  if (!crumb) return null;

  /**
   * ⛔ IT USED TO ASSERT A BUG FOR THE COMMONEST HARMLESS CASE (fixed 2026-10-24). Any request
   * that carried no token read as "your session had probably not expired — this is the bug being
   * tracked", so a device that was simply not signed in announced a defect. The operator spent an
   * evening on it; the breadcrumbs showed two entries, both `sentAuthHeader: false`, which is
   * precisely the case that proves NOTHING was refused.
   *
   * ⭐ `authAbsentReason` is the field that tells them apart, and it was already being collected
   * and already being printed three lines below. Three outcomes, three sentences:
   *
   *   no-token-in-storage   nothing was stored here. Sign in; nothing is wrong.
   *   poisoned-token-value  a corrupt value was stored and has been cleared.
   *   (token WAS sent)      something refused it — and that is the bug worth reporting.
   */
  const absent = crumb.authAbsentReason || null;
  /**
   * ⭐ THE CLIENT-SIDE CASE COMES FIRST, and it is the commonest of the three. sentAuthHeader
   * is null for it — nothing was sent because nothing was asked — which must not fall through
   * to the "the server rejected your token" branch and report a defect for a plain expiry.
   */
  const plain = crumb.clientReason
    ? (crumb.expired === true
      ? 'Your sign-in had lapsed, so this browser ended the session before contacting the server. Tokens last 12 hours and only renew while you are active — an overnight gap, or a laptop left asleep, uses one up. Nothing is wrong.'
      : 'This browser would not accept the sign-in it had stored, so it cleared it. Signing in again replaces it.')
    : crumb.sentAuthHeader === false
    ? (absent === 'no-token-in-storage'
      ? 'You were not signed in on this device, so the request went out without a token. Nothing is wrong — signing in below is all this needs.'
      : (absent && absent.startsWith('poisoned-token-value')
        ? 'The sign-in stored in this browser was unreadable, so it has been cleared. Signing in again replaces it.'
        : 'A request was sent without your sign-in token. Your session may have lapsed in an open tab; signing in again restores it.'))
    : crumb.serverReason === 'expired'
      ? 'Your sign-in token had expired. Tokens last 12 hours and only renew while you are active, so an overnight gap lapses one.'
      : `The server rejected the token this browser sent (${crumb.serverReason || 'reason not reported'}). ⛔ That is worth reporting.`;

  return (
    <Message warning size="small" style={{ textAlign: 'left' }}>
      <Message.Header>Why you were signed out</Message.Header>
      <p style={{ marginBottom: '0.4em' }}>{plain}</p>
      <div style={{ fontSize: '0.82em', fontFamily: 'monospace', opacity: 0.85, lineHeight: 1.5 }}>
        {new Date(crumb.at).toLocaleTimeString()}
        {crumb.url ? <> · {crumb.method} {crumb.url}</> : <> · no request was made</>}
        {/* ⭐ WHERE it happened, which is what identified the stray crumb of 2026-10-26: a request
            from /logout is a request that raced a sign-out, not a session that was refused. */}
        {crumb.page ? <> · from {crumb.page}</> : null}<br />
        sentAuthHeader={String(crumb.sentAuthHeader)} · serverReason={crumb.serverReason || '—'}<br />
        {crumb.clientReason ? <>clientReason={crumb.clientReason}<br /></> : null}
        {crumb.authAbsentReason ? <>authAbsentReason={crumb.authAbsentReason}<br /></> : null}
        token={crumb.tokenShape}{crumb.expiresAt ? ` · expired=${crumb.expired}` : ''}
      </div>
      <p style={{ fontSize: '0.82em', marginTop: '0.5em', opacity: 0.8 }}>
        Full history in the console: <code>readAuthBreadcrumbs()</code>
      </p>
    </Message>
  );
}

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userProfile = useSelector((state) => state.user.profile);

  /**
   * ─── ⛔ CLEAR A DEAD TOKEN, NEVER A LIVE ONE (2026-10-27) ──────────────────────────────────────
   *
   * This cleared `authToken` unconditionally on mount. Caught by the write audit on its first
   * real firing: 2026-10-27T04:57:39Z, a token with TWELVE HOURS of life left deleted by a mount
   * effect on /login, two minutes after it was issued — `downgrade: true`, `deliberate: false`.
   *
   * ⭐ WHY THAT IS SO DESTRUCTIVE. Arriving at /login does not mean the session is over; it means
   * something sent us here, and the commonest sender is a transient failure. Destroying a good
   * token on arrival turns a recoverable bounce into a real sign-out, and then into a loop: with
   * storage empty every protected route sees a GUEST, takes the soft redirect, and records
   * nothing — which is the operator's long-standing "seems to log in, but clicking any link kicks
   * back to login".
   *
   * ⓘ The hygiene this was written for is kept. A dead or poisoned value genuinely should not
   * survive a visit to the login form — it is what made `Bearer null` persist for so long — and
   * `isTokenLive` is the same check ProtectedRoute uses, so the two cannot disagree. A live token
   * needs no clearing anyway: signing in overwrites it.
   */
  React.useEffect(() => {
    const stored = localStorage.getItem('authToken');
    if (stored !== null && !isTokenLive(stored)) {
      localStorage.removeItem('authToken');
    }
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');

    /**
     * ⭐ READ THE DOM AS WELL AS THE STATE — a controlled input and a browser autofill
     * disagree, 2026-10-25.
     *
     * Safari (and Chrome) write an autofilled value straight into the DOM node. They do not
     * always dispatch an event React can see, so `email` and `password` stay at their initial
     * '' while the boxes on screen look full. The guard below then refused a login the operator
     * could plainly see credentials for, and said the credentials were WRONG — all without
     * making a request, which is why the failure left no 401 and no breadcrumb to find it by.
     *
     * ⓘ State first, DOM second. What the user typed is authoritative; the element value is
     * only consulted when state is empty, which is exactly the desync case.
     */
    const el = (e.target && e.target.elements) || {};
    const typedEmail = email || (el.email ? el.email.value : '');
    const typedPassword = password || (el.password ? el.password.value : '');

    // ⛔ NOT "incorrect". An empty box is not a rejected credential, and saying so sent the
    // operator looking for a password problem that was never there.
    if (!typedEmail.trim() || !typedPassword.trim()) {
      setLoginError('Please enter your email and password.');
      return;
    }

    try {
      let loginResults = await dispatch(loginUser({
        email: typedEmail.trim(),
        password: typedPassword,
      })).unwrap();
      if (loginResults.token) {
        localStorage.setItem('authToken', loginResults.token);

        // Use the primary role from the login response
        const primaryRole = loginResults.primaryRole || 'voiceActor'; // Default to voiceActor if no primary role

        // Set the active role in Redux store
        dispatch(switchActiveRole(primaryRole));

        // Redirect to the appropriate role-specific landing page
        const rolePaths = {
          admin: '/landing/admin',
          bookCreator: '/landing/book-creator',
          voiceActor: '/landing/voice-actor'
        };

        navigate(rolePaths[primaryRole] || '/landing/voice-actor');
      }
    }
    catch (error) {
      console.log('Invalid login', error);
      setLoginError('Either the email or password is incorrect.');
    }
  };

  const loginPage = () => {
    return (
      <div className='regLoginContainer' style={{ marginBottom: "70px" }}>
        <span className='formHeading'>Login</span>
        <br />
        <form onSubmit={handleSubmit} style={{ marginTop: "30px" }}>
          <span className='inputLabel'>Email:</span>
          {/**
            * ⭐ name + autoComplete, OR THE BROWSER GUESSES. Both fields carried an `id` and
            * nothing else, so Safari had no way to tell which box was the account: it filled a
            * generic keychain entry of its own making — the literal word "user" — instead of the
            * address that was saved. These two tokens are the documented way to say which is
            * which, and they also make the browser store the real email next time.
            */}
          {/**
            * ⛔ NOT type="email" — it was eating the Return key (operator, 2026-10-26: "the
            * password field is eating the carriage return...but not always").
            *
            * A form refuses implicit submission while a constrained field holds a value it
            * considers invalid, and shows a native bubble that is easy to miss entirely. So
            * Return did nothing, silently, and said nothing — and it was this same field that
            * a password manager had been filling with the word "user".
            *
            * ⓘ inputMode keeps the @ key on a phone keyboard. The validating is ours to do and
            * ours to report, in a message the user can actually read.
            */}
          <Input
            className='regLoginInput marginBottom_10'
            placeholder="Email address"
            type="text"
            inputMode="email"
            id="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <span className='inputLabel'>Password:</span>
          {/**
            * ⓘ current-password, not "password": it tells the manager to OFFER a saved one.
            * new-password is for a registration form and suppresses the offer.
            *
            * ⛔ A comment cannot sit between JSX attributes — neither // nor an expression
            * container is legal inside an opening tag. It goes here, above it.
            */}
          <Input
            className="floatLeft regLoginInput marginBottom_10"
            placeholder="Password"
            type="password"
            id="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <br />
          <Button className="floatLeft" style={{ clear: "both", marginTop: "10px" }} type="submit">Login</Button>
        </form>
        {/* Phase C.5 (Guest Mode) — the "Register Now" CTA is suppressed
            on builds where REACT_APP_REGISTRATION_ENABLED=false (the
            deployed WIP preview). The /register URL stays valid so an
            operator can hand out a direct link, but the public CTA is
            hidden so guests don't hit a 503. See client/src/utils/
            featureFlags.js for the fail-open semantics. */}
        {isRegistrationEnabled() && (
          <div style={{ clear: "both", paddingTop: "10px", fontSize: "1.1rem" }}>
            <p>Not registered yet? <Link to="/register">Register Now</Link></p>
          </div>
        )}
        <div className="floatLeft marginTop_10">
          {userProfile && userProfile.error && <Message negative>Error: {userProfile.error}</Message>}
          {userProfile && userProfile.loading && <Message>Loading...</Message>}
          {loginError && loginError !== '' && <Message negative>{loginError}</Message>}
          {/* Why the last redirect happened — survives the page load that erased the console. */}
          <WhySignedOut />
        </div>
      </div>
    );
  }
  return (loginPage())
}
